import { BAD, WARN, ACCENT } from '../../lib/uiTokens';
import { fmtKratce } from './bankaSdilene';

/**
 * Blok „K dořešení" pro třetí sloupec Banky (norma `standard-rozvrzeni.md` §1f).
 *
 * Stejný vzor jako ve Směnách a ve Výsledovce: nadpis sekce, pak barevná
 * upozornění s číslem a s tím, co se s tím dá udělat. Pravidla nálezů počítá
 * backend (`BankController::statistiky`), aby se počet v pilulce pohledu
 * a obsah seznamu nemohly rozejít — tady se z nich jen skládá česká věta.
 *
 * Vrací max tři položky: pod panelem 320 px ani na telefonu není na víc místo
 * a delší seznam přestane být seznam nálezů a začne být další tabulka.
 *
 * @returns {{barva: string, titulek: string, detail: string, pohled?: string}[]}
 */
export function sestavNalezy(staty) {
    if (!staty) return [];
    const nalezy = [];
    const prah = staty.prah_starych_dnu ?? 14;

    // Výpisy na sebe nenavazují — chybějící výpis znamená pohyby, které v bance
    // vůbec nejsou, takže je žádný jiný nález nenajde. Proto jde první.
    const vypisy = nalezVypisu(staty);
    if (vypisy) nalezy.push(vypisy);

    if (staty.stare_nesparovane > 0) {
        nalezy.push({
            barva: BAD,
            pohled: 'stare',
            titulek: `${staty.stare_nesparovane} ${pohybu(staty.stare_nesparovane)} starších ${prah} dní bez párování`,
            detail: `Dohromady ${fmtKratce(staty.stare_nesparovane_kc)} Kč. Čím starší platba, tím hůř se dohledá, ke které faktuře patřila.`,
        });
    }

    if (staty.prichozi_bez_zarazeni > 0) {
        nalezy.push({
            barva: WARN,
            pohled: 'bez_zarazeni',
            titulek: `${staty.prichozi_bez_zarazeni} ${prichozich(staty.prichozi_bez_zarazeni)} bez zařazení`,
            detail: 'Nezařazený příjem se neobjeví ve Výsledovce ani v DPH — tiše chybí na obou místech.',
        });
    }

    // Nespárované celkem hlásíme, jen když to není už pokryté nálezem výš —
    // dvě upozornění o téže hromadě by z panelu udělala seznam.
    const zbyva = (staty.nesparovano ?? 0) - (staty.stare_nesparovane ?? 0);
    if (zbyva > 0 && nalezy.length < 3) {
        nalezy.push({
            barva: ACCENT,
            pohled: 'neparovano',
            titulek: `${zbyva} ${pohybu(zbyva)} čeká na spárování`,
            detail: 'Tlačítko „Projít pohyby" na ně pustí pravidla i AI a návrhy pak jen potvrdíš.',
        });
    }

    return nalezy.slice(0, 3);
}

const fmtD = (d) => (d ? new Date(d).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }) : '?');

/**
 * Jeden souhrnný nález z kontroly výpisů (`VypisyKontrola`): mezera mezi
 * výpisy, nesedící zůstatek, neúplný import, nebo dlouho nenahraný výpis.
 * Bere nejzávažnější a jmenuje účet — oprava je vždy „nahraj výpis".
 */
function nalezVypisu(staty) {
    const ucty = staty.vypisy || [];
    for (const u of ucty) {
        if (u.mezery?.length) {
            const m = u.mezery[0];
            return {
                barva: BAD,
                titulek: `${u.ucet}: chybí výpis ${fmtD(m.od)} – ${fmtD(m.do)}`,
                detail: u.mezery.length > 1
                    ? `A další ${u.mezery.length - 1} ${u.mezery.length - 1 === 1 ? 'mezera' : 'mezery'}. Pohyby z chybějícího výpisu v bance nejsou vůbec — nahraj ho.`
                    : 'Pohyby z toho období v bance nejsou vůbec, takže je nic nespáruje ani nezařadí. Nahraj chybějící výpis.',
            };
        }
    }
    for (const u of ucty) {
        if (u.nesoulady?.length) {
            const n = u.nesoulady[0];
            return {
                barva: BAD,
                titulek: `${u.ucet}: zůstatek nenavazuje (výpis č. ${n.cislo_vypisu})`,
                detail: `Výpis začíná na ${fmtKratce(n.pocatecni)} Kč, předchozí končil na ${fmtKratce(n.ocekavano)} Kč. Nejspíš jde o výpis jiného účtu.`,
            };
        }
        if (u.neuplne?.length) {
            const n = u.neuplne[0];
            return {
                barva: BAD,
                titulek: `${u.ucet}: výpis č. ${n.cislo_vypisu} se nenačetl celý`,
                detail: 'Součet pohybů nesedí na obraty v záhlaví výpisu. Pošli soubor k prověření.',
            };
        }
    }
    const stary = ucty.find(u => u.zastaraly);
    if (stary) {
        return {
            barva: WARN,
            titulek: `${stary.ucet}: poslední výpis k ${fmtD(stary.pokryto_do)}`,
            detail: `Novější výpis ještě není nahraný — platby za posledních ${staty.prah_aktualnosti_dnu ?? 10}+ dní se nespárují.`,
        };
    }
    return null;
}

const pohybu = (n) => (n === 1 ? 'pohyb' : (n >= 2 && n <= 4 ? 'pohyby' : 'pohybů'));
const prichozich = (n) => (n === 1 ? 'příchozí platba' : (n >= 2 && n <= 4 ? 'příchozí platby' : 'příchozích plateb'));
